import { z } from 'zod';
import { SessionUserSchema } from './auth.js';
import { TrackSchema } from './track.js';
// The false arm covers two different states, which is why `track`/`progressMs` are optional on it
// rather than absent: a player paused on a track carries both (Spotify keeps reporting the item and
// its position), a genuinely idle one — Spotify closed, a 204, nothing loaded — carries neither.
// `isPlaying` alone says whether the music is moving; a caller must never read the presence of a
// track as "playing", and anything that credits elapsed time or projects forward has to key on the
// discriminant, not on `track`.
export const NowPlayingSchema = z
    .discriminatedUnion('isPlaying', [
    z.strictObject({
        isPlaying: z.literal(true),
        track: TrackSchema,
        progressMs: z.number().int().nonnegative(),
    }),
    z.strictObject({
        isPlaying: z.literal(false),
        track: TrackSchema.optional(),
        progressMs: z.number().int().nonnegative().optional(),
    }),
])
    .readonly();
// unavailable is a value, not an error: Spotify permanently 403s this without Premium, so it isn't something to retry.
export const SpotifyQueueSchema = z
    .discriminatedUnion('status', [
    z.strictObject({
        status: z.literal('ok'),
        items: z.array(TrackSchema).readonly(),
    }),
    z.strictObject({
        status: z.literal('unavailable'),
        reason: z.literal('premium_required'),
    }),
])
    .readonly();
export const NowPlayingBoardEntrySchema = z
    .strictObject({
    user: SessionUserSchema,
    nowPlaying: NowPlayingSchema,
})
    .readonly();
export const NowPlayingBoardSchema = z.array(NowPlayingBoardEntrySchema).readonly();
// Checked strictly, unlike the looser `TrackSchema.id`, which just snapshots whatever Spotify reports.
// This id comes from a link somebody typed, so it's validated rather than trusted.
const SPOTIFY_TRACK_ID_PATTERN = /^[A-Za-z0-9]{22}$/u;
export const SpotifyTrackIdSchema = z
    .string()
    .regex(SPOTIFY_TRACK_ID_PATTERN)
    .brand();
/** Comfortably above the longest link Spotify's share button produces. */
export const SPOTIFY_TRACK_LINK_MAX_LENGTH = 512;
// Anchored at both ends with no unbounded quantifiers — provably linear, so a hostile string can't make matching expensive.
// The localised path is its own pattern (not an optional group) to keep that guarantee simple.
const TRACK_LINK_PATTERNS = [
    /^https:\/\/open\.spotify\.com\/track\/(?<id>[A-Za-z0-9]{22})$/u,
    /^https:\/\/open\.spotify\.com\/intl-[a-z]{2}\/track\/(?<id>[A-Za-z0-9]{22})$/u,
    /^spotify:track:(?<id>[A-Za-z0-9]{22})$/u,
];
// ?si= is Spotify's share-tracking param and says nothing about the track, so it's stripped rather than matched.
const withoutQuery = (value) => value.split(/[?#]/u, 1)[0] ?? value;
const readTrackId = (value) => TRACK_LINK_PATTERNS.map((pattern) => pattern.exec(withoutQuery(value))?.groups?.['id']).find((id) => id !== undefined);
const readTrackRef = (value) => SPOTIFY_TRACK_ID_PATTERN.test(value) ? value : readTrackId(value);
export const SpotifyTrackRefSchema = z
    .string()
    .trim()
    .min(1)
    .max(SPOTIFY_TRACK_LINK_MAX_LENGTH)
    .refine((value) => readTrackRef(value) !== undefined, 'must be a Spotify track id or link, e.g. https://open.spotify.com/track/3n3Ppam7vgaVa1iaRUc9Lp')
    // ?? '' is safe here: the refine above already rejects anything that would reach it, so this never actually produces an empty id.
    .transform((value) => readTrackRef(value) ?? '')
    .pipe(SpotifyTrackIdSchema);
export const spotifyTrackUrl = (trackId) => `https://open.spotify.com/track/${trackId}`;
//# sourceMappingURL=spotify.js.map